import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  InputNumber,
  Card,
  Alert,
  Typography,
  Space,
  Select,
} from "antd";
import { CompanyService } from "../../services/CompanyService";
import { usePermissions } from "../../PermissionsProvider";
import { CloudUploadOutlined } from "@ant-design/icons";
import { Company } from "../../model_gen/company";

const { Title, Paragraph } = Typography;
const { Option } = Select;

interface IngestFormValues {
  bucket: string;
  requestPrefix: string;
  responsePrefix: string;
  companyId: number;
  maxConcurrent: number;
}

const IngestData: React.FC = () => {
  const [form] = Form.useForm();
  const { apiKey } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  // Load affiliated companies on component mount
  useEffect(() => {
    setLoadingCompanies(true);
    CompanyService.getAffiliatedCompanies(
      setLoadingCompanies,
      apiKey
    ).subscribe(
      (data: Company[]) => {
        setCompanies(data);
        // If there's only one company, select it automatically
        if (data.length === 1 && data[0].id) {
          form.setFieldsValue({ companyId: data[0].id });
        }
      },
      (error: any) => {
        console.error("Error loading companies:", error);
        setError("Failed to load companies. Please try again.");
      }
    );
  }, [apiKey, form]);

  const handleIngest = (values: IngestFormValues) => {
    setLoading(true);
    setSuccess(false);
    setError(null);

    const ingestPayload = {
      bucket: values.bucket,
      request_prefix: values.requestPrefix,
      response_prefix: values.responsePrefix,
      company_id: values.companyId,
    };

    CompanyService.ingestChatCompletion(
      values.companyId,
      ingestPayload,
      setLoading,
      apiKey
    ).subscribe(
      (response: any) => {
        setSuccess(true);
        console.log("Ingestion job started:", response);
      },
      (err: any) => {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
        console.error("Ingestion error:", err);
      }
    );
  };

  return (
    <Card title="S3 Data Ingestion" className="ingest-data-card">
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        <div>
          <Title level={4}>Ingest Chat Completion Data from S3</Title>
          <Paragraph>
            This tool allows you to ingest chat completion data from S3 into the
            Maitai platform. The system will read request and response pairs
            from S3, match them by filename, and push them to Kinesis for
            processing.
          </Paragraph>
        </div>

        {success && (
          <Alert
            message="Ingestion Started"
            description="The ingestion job has been successfully started. You can check the logs for progress updates."
            type="success"
            showIcon
            closable
          />
        )}

        {error && (
          <Alert
            message="Ingestion Error"
            description={error}
            type="error"
            showIcon
            closable
            onClose={() => setError(null)}
          />
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleIngest}
          initialValues={{}}>
          <Form.Item
            name="bucket"
            label="S3 Bucket Name"
            rules={[
              { required: true, message: "Please enter the S3 bucket name" },
            ]}>
            <Input placeholder="my-bucket" />
          </Form.Item>

          <Form.Item
            name="requestPrefix"
            label="Request Prefix"
            rules={[
              { required: true, message: "Please enter the request prefix" },
            ]}>
            <Input placeholder="data/requests/" />
          </Form.Item>

          <Form.Item
            name="responsePrefix"
            label="Response Prefix"
            rules={[
              { required: true, message: "Please enter the response prefix" },
            ]}>
            <Input placeholder="data/responses/" />
          </Form.Item>

          <Form.Item
            name="companyId"
            label="Company"
            rules={[{ required: true, message: "Please select a company" }]}>
            <Select
              placeholder="Select a company"
              loading={loadingCompanies}
              disabled={loadingCompanies}>
              {companies.map((company) => (
                <Option key={company.id} value={company.id}>
                  {company.company_name || `Company ID: ${company.id}`}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="maxConcurrent"
            label="Max Concurrent Operations"
            tooltip="Maximum number of concurrent file operations (default: 10)">
            <InputNumber min={1} max={50} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              icon={<CloudUploadOutlined />}
              loading={loading}
              block>
              Start Ingestion
            </Button>
          </Form.Item>
        </Form>
      </Space>
    </Card>
  );
};

export default IngestData;
